import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import KazemLogo from "@/components/KazemLogo";
import { LayoutDashboard, ClipboardList, Truck, FileText, Mail, LogOut, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const LINKS = [
    { to: "/tms", end: true, icon: LayoutDashboard, label: "Tableau de bord", testid: "nav-tms-dashboard" },
    { to: "/tms/ot", icon: ClipboardList, label: "Ordres de transport", testid: "nav-tms-ot" },
    { to: "/tms/invoices", icon: FileText, label: "Facturation", testid: "nav-tms-invoices" },
    { to: "/tms/carriers", icon: Truck, label: "Transporteurs", testid: "nav-tms-carriers" },
    { to: "/tms/quotes", icon: Mail, label: "Demandes de devis", testid: "nav-tms-quotes" },
];

export default function TmsLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const onLogout = async () => {
        await logout();
        toast.success("Déconnecté");
        navigate("/tms/login");
    };

    return (
        <div className="min-h-screen bg-[#f4f6fa] text-[#0b2545] flex">
            <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-[#0b2545] sticky top-0 h-screen">
                <div className="px-5 py-6 border-b border-blue-100/10"><KazemLogo light /></div>
                <nav className="flex-1 px-3 py-5 space-y-1">
                    {LINKS.map((l) => (
                        <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testid}
                            className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm transition-colors ${isActive ? "bg-[#f26b1d] text-[#fff]" : "text-blue-100/80 hover:bg-blue-100/10 hover:text-[#fff]"}`}>
                            <l.icon size={16} /> {l.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-blue-100/10">
                    <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-blue-100/70 hover:text-[#fff] mb-3">
                        <ExternalLink size={12} /> Voir le site public
                    </a>
                    <div className="rounded-md bg-blue-100/10 p-3">
                        <div className="text-xs text-blue-100/60">Connecté</div>
                        <div className="text-sm font-medium text-[#fff] mt-0.5" data-testid="tms-user-name">{user?.name}</div>
                        <div className="text-xs text-blue-100/70 truncate">{user?.email}</div>
                        <button onClick={onLogout} data-testid="tms-logout-button" className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded text-xs text-[#fff] bg-[#f26b1d] hover:bg-[#d95a10]">
                            <LogOut size={12} /> Déconnexion
                        </button>
                    </div>
                </div>
            </aside>

            <div className="lg:hidden fixed top-0 inset-x-0 bg-[#0b2545] z-40 flex items-center justify-between px-4 py-2.5">
                <KazemLogo light />
                <button onClick={onLogout} data-testid="tms-logout-button-mobile" className="text-blue-100" aria-label="Déconnexion"><LogOut size={16} /></button>
            </div>

            <div className="flex-1 min-w-0 lg:pt-0 pt-16 pb-16 lg:pb-0">
                <Outlet />
                <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 grid grid-cols-5 z-30">
                    {LINKS.map((l) => (
                        <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `flex flex-col items-center gap-1 py-2 text-[10px] ${isActive ? "text-[#f26b1d]" : "text-slate-600"}`}>
                            <l.icon size={16} />
                            <span className="truncate px-1 max-w-full">{l.label.split(" ")[0]}</span>
                        </NavLink>
                    ))}
                </nav>
            </div>
        </div>
    );
}
